import {
  AUDIENCE_LABELS,
  BRAND,
  LIMITS,
  announcementVisibleTo,
  validateAnnouncement,
  whatsappShareUrl,
  type AnnouncementAudience,
} from '@crew/shared';
import { useMemo, useState } from 'react';
import { useAdmin } from '@/auth/AuthProvider';
import { useJobRegistrations, type JobView, type RegistrationView } from '@/hooks/useActiveJobs';
import { useAnnouncements, type AnnouncementView } from '@/hooks/useMessaging';
import { formatWhen } from '@/lib/activity';
import { useSystemSettings } from '@/hooks/useSettings';
import { tsToDate } from '@/lib/ts';
import { adminApi } from '@/services/adminApi';
import { errorMessage } from '@/services/apiError';
import { EditIcon, MegaphoneIcon, PlusIcon, TrashIcon, WhatsAppIcon } from '../icons';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Notice } from '../ui/Notice';
import { Spinner } from '../ui/Spinner';
import { useToast } from '../ui/Toast';
import { TypeChip } from './RegistrationsPanel';

/**
 * עדכונים מהמנהלים לנרשמים בעבודה.
 * העדכון מופיע בקישור האישי של מי שהוא מיועד לו (עם תג "חדש" עד שנצפה),
 * ואם סומן — גם במסך ההרשמה. אין שליחה אוטומטית: לשיתוף בווטסאפ יש כפתור.
 */
export function AnnouncementsCard({ job }: { job: JobView }) {
  const anns = useAnnouncements(job.id);
  const regs = useJobRegistrations(job.id);
  const [editing, setEditing] = useState<AnnouncementView | 'new' | null>(null);
  const [seenOf, setSeenOf] = useState<AnnouncementView | null>(null);
  const active = job.status === 'active';
  const regList = regs.state === 'ready' ? regs.data : [];

  return (
    <section className="rounded-card border border-line bg-surface p-4 md:p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-[17px] font-bold">
          <MegaphoneIcon size={20} className="text-accent" />
          עדכונים לנרשמים
        </h2>
        {active && (
          <Button className="min-h-10 px-3 text-[14px]" icon={<PlusIcon size={17} />} onClick={() => setEditing('new')}>
            עדכון חדש
          </Button>
        )}
      </div>

      {anns.state === 'loading' && (
        <div className="flex justify-center py-5">
          <Spinner />
        </div>
      )}
      {anns.state === 'error' && <Notice tone="error">{anns.message}</Notice>}
      {anns.state === 'ready' && anns.data.length === 0 && (
        <p className="text-[14px] text-ink-3">
          {active ? 'עדכון שתכתבו כאן יופיע בקישור האישי של הנרשמים — למשל שינוי שער כניסה או בקשה לרשימת שמות.' : 'לא פורסמו עדכונים.'}
        </p>
      )}
      {anns.state === 'ready' && anns.data.length > 0 && (
        <ul className="space-y-3">
          {anns.data.map((a) => (
            <AnnouncementItem key={a.id} job={job} a={a} regs={regList} active={active} onEdit={() => setEditing(a)} onSeen={() => setSeenOf(a)} />
          ))}
        </ul>
      )}

      <AnnouncementDialog job={job} editing={editing} regs={regList} onClose={() => setEditing(null)} />
      <SeenDialog a={seenOf} regs={regList} onClose={() => setSeenOf(null)} />
    </section>
  );
}

function audienceOf(a: AnnouncementView, regs: RegistrationView[]) {
  const targets = regs.filter((r) => announcementVisibleTo(a, { contractorId: r.id, registrantType: r.registrantType }));
  const since = tsToDate(a.updatedAt)?.getTime() ?? 0;
  const seen = targets.filter((r) => (tsToDate(r.announcementsSeenAt)?.getTime() ?? 0) >= since);
  return { targets, seen };
}

export function announcementShareText(jobName: string, text: string, signature: string = BRAND.whatsapp.signature): string {
  return `*${BRAND.name}*\nעדכון — ${jobName}\n\n${text}\n\nהעדכון מופיע גם בקישור האישי שלכם.\n\n${signature}`;
}

function AnnouncementItem({
  job,
  a,
  regs,
  active,
  onEdit,
  onSeen,
}: {
  job: JobView;
  a: AnnouncementView;
  regs: RegistrationView[];
  active: boolean;
  onEdit: () => void;
  onSeen: () => void;
}) {
  const { user } = useAdmin();
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const { targets, seen } = audienceOf(a, regs);
  const { whatsappSignature: signature } = useSystemSettings();
  const created = tsToDate(a.createdAt);
  const edited = tsToDate(a.updatedAt);
  const wasEdited = created && edited && edited.getTime() - created.getTime() > 1000;
  const audience = a.audience === 'selected' ? `${a.recipientIds.length} נרשמים נבחרים` : AUDIENCE_LABELS[a.audience];

  async function remove() {
    setBusy(true);
    try {
      await adminApi(user).deleteAnnouncement(job.id, a.id);
      toast('העדכון נמחק');
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }

  return (
    <li className="rounded-control border border-line bg-paper p-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-ink-3">
        <span className="rounded-full bg-brand-soft px-2 py-0.5 font-semibold text-brand-strong">{audience}</span>
        {a.showOnRegistration && <span className="rounded-full bg-accent-soft px-2 py-0.5 font-semibold text-accent">גם במסך ההרשמה</span>}
        <span>
          {a.createdByName} · {created ? formatWhen(created) : ''}
          {wasEdited && ' · נערך'}
        </span>
      </div>
      <p className="whitespace-pre-line text-[15px] leading-relaxed">{a.text}</p>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <button type="button" onClick={onSeen} className="me-auto text-[13px] font-medium text-ink-2 underline-offset-2 hover:underline">
          נצפה {seen.length}/{targets.length}
        </button>
        <a
          href={whatsappShareUrl(announcementShareText(job.name, a.text, signature))}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-9 items-center gap-1.5 rounded-control px-2.5 text-[13px] font-semibold text-[#128C7E] hover:bg-sunken"
        >
          <WhatsAppIcon size={16} />
          שיתוף
        </a>
        {active && (
          <>
            <button type="button" onClick={onEdit} className="inline-flex min-h-9 items-center gap-1.5 rounded-control px-2.5 text-[13px] font-semibold text-brand hover:bg-sunken">
              <EditIcon size={16} />
              עריכה
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              aria-label="מחיקת העדכון"
              className="inline-flex size-9 items-center justify-center rounded-control text-danger hover:bg-danger-soft"
            >
              <TrashIcon size={16} />
            </button>
          </>
        )}
      </div>
      {confirmDelete && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-control bg-danger-soft p-2.5 text-[14px]">
          <span className="flex-1 font-semibold text-danger">למחוק את העדכון? הוא ייעלם מהקישורים של הנרשמים.</span>
          <Button variant="danger" className="min-h-9 px-3 text-[14px]" loading={busy} onClick={remove}>
            מחיקה
          </Button>
          <Button variant="secondary" className="min-h-9 px-3 text-[14px]" onClick={() => setConfirmDelete(false)}>
            ביטול
          </Button>
        </div>
      )}
    </li>
  );
}

const AUDIENCE_OPTIONS: AnnouncementAudience[] = ['all', 'contractors', 'workers', 'selected'];

function AnnouncementDialog({
  job,
  editing,
  regs,
  onClose,
}: {
  job: JobView;
  editing: AnnouncementView | 'new' | null;
  regs: RegistrationView[];
  onClose: () => void;
}) {
  const { user } = useAdmin();
  const toast = useToast();
  const [text, setText] = useState('');
  const [audience, setAudience] = useState<AnnouncementAudience>('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [onRegistration, setOnRegistration] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadedFor, setLoadedFor] = useState<AnnouncementView | 'new' | null>(null);

  // מילוי הטופס כשנפתח (עדכון קיים או חדש)
  if (editing !== loadedFor) {
    setLoadedFor(editing);
    const a = editing && editing !== 'new' ? editing : null;
    setText(a?.text ?? '');
    setAudience(a?.audience ?? 'all');
    setPicked(new Set(a?.recipientIds ?? []));
    setOnRegistration(a?.showOnRegistration ?? false);
    setError(null);
    setBusy(false);
  }

  const sortedRegs = useMemo(() => [...regs].sort((a, b) => a.fullName.localeCompare(b.fullName, 'he')), [regs]);
  if (!editing) return null;
  const isNew = editing === 'new';

  async function save() {
    const body = { text, audience, recipientIds: [...picked], showOnRegistration: onRegistration };
    const v = validateAnnouncement(body);
    if (!v.ok) return setError(v.error);
    setBusy(true);
    setError(null);
    try {
      const api = adminApi(user);
      if (isNew) await api.createAnnouncement(job.id, body);
      else await api.updateAnnouncement(job.id, (editing as AnnouncementView).id, body);
      toast(isNew ? 'העדכון פורסם בקישורים של הנרשמים' : 'העדכון נשמר');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? 'עדכון חדש לנרשמים' : 'עריכת עדכון'}
      footer={
        <div className="flex gap-2">
          <Button block loading={busy} onClick={save}>
            {isNew ? 'פרסום' : 'שמירה'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
        </div>
      }
    >
      <label className="block">
        <span className="mb-1.5 block text-[14px] font-semibold">תוכן העדכון</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          maxLength={LIMITS.maxAnnouncementLength}
          placeholder="לדוגמה: הכניסה לצוות דרך שער 3. חניה בחניון B."
          className="w-full rounded-control border border-line-strong bg-surface px-3 py-2.5 text-[16px] leading-relaxed focus:border-brand focus:outline-none"
        />
        <span className="mt-1 block text-start text-[12px] text-ink-3">
          {text.length}/{LIMITS.maxAnnouncementLength}
        </span>
      </label>

      <fieldset className="mt-3">
        <legend className="mb-1.5 text-[14px] font-semibold">למי</legend>
        <div className="grid grid-cols-2 gap-2">
          {AUDIENCE_OPTIONS.map((o) => (
            <label
              key={o}
              className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-control border px-3 text-[14px] ${audience === o ? 'border-brand bg-brand-soft font-semibold' : 'border-line-strong'}`}
            >
              <input type="radio" name="audience" checked={audience === o} onChange={() => setAudience(o)} className="accent-[var(--color-brand)]" />
              {AUDIENCE_LABELS[o]}
            </label>
          ))}
        </div>
      </fieldset>

      {audience === 'selected' && (
        <div className="mt-3 max-h-56 overflow-y-auto rounded-control border border-line">
          {sortedRegs.length === 0 && <p className="p-3 text-[14px] text-ink-3">אין עדיין נרשמים לעבודה.</p>}
          {sortedRegs.map((r) => (
            <label key={r.id} className="flex min-h-11 cursor-pointer items-center gap-2.5 border-b border-line px-3 last:border-b-0">
              <input
                type="checkbox"
                checked={picked.has(r.id)}
                onChange={(e) => {
                  const next = new Set(picked);
                  if (e.target.checked) next.add(r.id);
                  else next.delete(r.id);
                  setPicked(next);
                }}
                className="size-4 accent-[var(--color-brand)]"
              />
              <TypeChip type={r.registrantType} />
              <span className="text-[15px]">{r.fullName}</span>
            </label>
          ))}
        </div>
      )}

      {audience === 'all' && (
        <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-control bg-paper p-3 text-[14px]">
          <input type="checkbox" checked={onRegistration} onChange={(e) => setOnRegistration(e.target.checked)} className="mt-0.5 size-4 accent-[var(--color-brand)]" />
          <span>
            <strong>להציג גם במסך ההרשמה</strong>
            <span className="block text-ink-2">מי שנכנס עם הקוד יראה את העדכון לפני שהוא נרשם (למשל "מחפשים עוד 5 עובדים").</span>
          </span>
        </label>
      )}

      {!isNew && <p className="mt-3 text-[13px] text-ink-3">אחרי שמירה העדכון יסומן שוב כ"חדש" אצל הנרשמים.</p>}
      {error && (
        <div className="mt-3">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
    </Dialog>
  );
}

function SeenDialog({ a, regs, onClose }: { a: AnnouncementView | null; regs: RegistrationView[]; onClose: () => void }) {
  if (!a) return null;
  const { targets, seen } = audienceOf(a, regs);
  const seenIds = new Set(seen.map((r) => r.id));
  const notSeen = targets.filter((r) => !seenIds.has(r.id));
  return (
    <Dialog open onClose={onClose} title="מי ראה את העדכון">
      <p className="mb-3 text-[14px] text-ink-2">"נצפה" = הנרשם פתח את הקישור האישי שלו אחרי שהעדכון פורסם או נערך.</p>
      <SeenList title={`ראו (${seen.length})`} regs={seen} tone="ok" />
      <div className="mt-4">
        <SeenList title={`עוד לא ראו (${notSeen.length})`} regs={notSeen} tone="muted" />
      </div>
    </Dialog>
  );
}

function SeenList({ title, regs, tone }: { title: string; regs: RegistrationView[]; tone: 'ok' | 'muted' }) {
  return (
    <div>
      <h3 className={`mb-1.5 text-[14px] font-semibold ${tone === 'ok' ? 'text-ok' : 'text-ink-2'}`}>{title}</h3>
      {regs.length === 0 ? (
        <p className="text-[13px] text-ink-3">—</p>
      ) : (
        <ul className="space-y-1">
          {regs.map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-[15px]">
              <TypeChip type={r.registrantType} />
              {r.fullName}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
